/**
 * CABModelSelector — Model tier picker for CAB generation.
 *
 * Lets users choose the LLM quality tier (Low / Medium / High) before
 * starting generation. Currently not rendered — CABPage always generates
 * with DEFAULT_MODEL_TIER. Kept so the picker can be re-enabled.
 */

import React from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

// ────────────────────────────────────────────────────────────
// Model tier definitions
// ────────────────────────────────────────────────────────────

export type CABModelTier = 'low' | 'medium' | 'high';

export const DEFAULT_MODEL_TIER: CABModelTier = 'high';

interface ModelTierOption {
  value: CABModelTier;
  label: string;
  description: string;
}

const MODEL_TIER_OPTIONS: ModelTierOption[] = [
  {
    value: 'low',
    label: 'Low',
    description: 'Fastest & most affordable',
  },
  {
    value: 'medium',
    label: 'Medium',
    description: 'Balanced speed & quality',
  },
  {
    value: 'high',
    label: 'High',
    description: 'Best quality output',
  },
];

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

interface CABModelSelectorProps {
  selectedTier: CABModelTier;
  onChange: (tier: CABModelTier) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

export const CABModelSelector: React.FC<CABModelSelectorProps> = ({
  selectedTier,
  onChange,
  disabled = false,
  readOnly = false,
}) => {
  return (
    <div className="mt-bg-v2-lg border border-border-secondary rounded-bg-v2-md overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-bg-v2-md py-bg-v2-sm bg-bg-secondary">
        <div className="flex items-center gap-bg-v2-sm">
          <Icon name={IconName.Sort} size="sm" className="text-fg-quaternary" inheritColor />
          <Text variant="text-xs" weight="medium" className="text-text-secondary">
            Model Quality
          </Text>
        </div>
      </div>

      {/* Tier options */}
      <div className="grid grid-cols-3 gap-bg-v2-sm px-bg-v2-md py-bg-v2-md">
        {MODEL_TIER_OPTIONS.map((option) => {
          const isSelected = selectedTier === option.value;
          const radioId = `cab-model-tier-${option.value}`;

          return (
            <label
              key={option.value}
              htmlFor={readOnly ? undefined : radioId}
              className={`
                relative flex flex-col items-center text-center
                px-bg-v2-sm py-bg-v2-sm rounded-bg-v2-md border
                transition-all duration-150 select-none
                ${isSelected
                  ? 'border-bg-primary-solid bg-bg-primary'
                  : 'border-border-primary bg-bg-primary hover:border-border-secondary hover:bg-bg-primary-hover'
                }
                ${readOnly ? '' : disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {/* Hidden radio */}
              {!readOnly && (
                <input
                  type="radio"
                  id={radioId}
                  name="cab-model-tier"
                  value={option.value}
                  checked={isSelected}
                  onChange={() => {
                    if (!disabled) onChange(option.value);
                  }}
                  disabled={disabled}
                  className="sr-only"
                />
              )}

              {/* Radio indicator - hidden in readOnly mode */}
              {!readOnly && (
                <span className={`
                  shrink-0 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center
                  transition-colors duration-150 mb-bg-v2-xxs
                  ${isSelected
                    ? 'border-bg-primary-solid'
                    : 'border-border-primary'
                  }
                `}>
                  {isSelected && (
                    <span className="w-2 h-2 rounded-full bg-bg-primary-solid" />
                  )}
                </span>
              )}

              <Text
                variant="text-xs"
                weight={isSelected ? 'semibold' : 'medium'}
                className="text-text-primary"
              >
                {option.label}
              </Text>
              <Text variant="text-xs" className="text-text-tertiary leading-tight mt-0.5">
                {option.description}
              </Text>
            </label>
          );
        })}
      </div>
    </div>
  );
};
